import React from "react";
import styled from "styled-components";
import {IconButton, Tooltip} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import LinkIcon from "@material-ui/icons/Link";
import {Skeleton} from "@material-ui/lab";

import TestStatus from "routes/llm_run/llm_run_list/components/test_status";
import {getQueryParams} from "routes/data_assets/data_assets_create_view/containers/data_assets_create_view";

import {DataAssetOverview} from "common/flowmaster/types/data_asset";
import {OrganisationId} from "common/types/organisation";

import ListLink from "common_components/flowmaster/llm_run/styled/ListLink";
import FilterableName from "common_components/flowmaster/llm_run/filtering/filterable_name";
import {DocumentId} from "common/types/document";
import {ProjectId} from "common/types/project";
import renderDuration from "routes/llm_run/utils/render_duration";
import getStatusIcon from "routes/data_assets/util/get_status_icon";

const Table = styled.table`
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  thead th {
    padding-bottom: 0.5em;
  }
  width: 100%;
`;

const TableRow = styled.tr<{$isSelected?: boolean}>`
  &:nth-child(even) {
    background-color: ${props => (props.$isSelected ? "#ccefff" : "#fcfcfc")};
  }
  &:nth-child(odd) {
    background-color: ${props => (props.$isSelected ? "#ccefff" : "#eef")};
  }
  &:hover {
    background-color: ${props => (props.$isSelected ? "#ccefff" : "#ffffdd")};
  }
  transition: background-color 0.2s ease;
`;

const TableCell = styled.td`
  padding: 1em 0.5em;
  border-top: 1px solid #aaa;
  text-align: center;
  background-color: inherit;
`;

const MediumCell = styled(TableCell)`
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MediumTh = styled.th`
  width: 10%;
`;

const StatsSubTh = styled.th`
  vertical-align: top;
  font-size: small;
`;

type FilterByRunEntity = (
  projectId: ProjectId,
  documentId?: DocumentId,
) => void;

type Props = {
  isLoading: boolean;
  selectedDataAssetRun: DataAssetOverview | null | undefined;
  dataAssetRuns: DataAssetOverview[];
  fetchRunsError: string | null;
  organisationId: OrganisationId;
  filterByRunEntity: FilterByRunEntity;
  updateFilterValue: (
    pairs: Record<string, unknown | undefined | null>,
  ) => void;
  refreshButtonRef: React.RefObject<HTMLButtonElement>;
  handleOpenOutput: (run: DataAssetOverview) => void;
  showOnlyTopLevelRuns: boolean;
};

const DataAssetRunsSkeleton = () => {
  return (
    <tr>
      <TableCell style={{padding: "1em 0em"}} colSpan={12}>
        <Skeleton height={"2em"} />
        <Skeleton height={"2em"} />
        <Skeleton height={"2em"} />
      </TableCell>
    </tr>
  );
};

export default function DataAssetRunsTableComponent({
  isLoading,
  fetchRunsError,
  dataAssetRuns,
  selectedDataAssetRun,
  organisationId,
  filterByRunEntity,
  updateFilterValue,
  refreshButtonRef,
  handleOpenOutput,
  showOnlyTopLevelRuns,
}: Props) {
  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        height: "100%",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <DataAssetRunsTable
        {...{
          dataAssetRuns,
          isLoading,
          error: fetchRunsError,
          organisationId,
          filterByRunEntity,
          onViewOutput: handleOpenOutput,
          selectedDataAssetRun,
          refreshButtonRef,
          updateFilterValue,
          showOnlyTopLevelRuns,
        }}
      />
    </div>
  );
}

function DataAssetRunsTable({
  dataAssetRuns,
  isLoading,
  error,
  organisationId,
  filterByRunEntity,
  onViewOutput,
  selectedDataAssetRun,
  updateFilterValue,
  showOnlyTopLevelRuns,
}) {
  return (
    <Table>
      <thead>
        <tr
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            zIndex: 1,
          }}
        >
          <MediumTh rowSpan={2}>Name</MediumTh>
          <MediumTh rowSpan={2}>Definition</MediumTh>
          <th style={{width: "12em"}} rowSpan={2}>
            Configuration
          </th>
          <MediumTh rowSpan={2}>Last Generated</MediumTh>
          <th style={{width: "12em"}} rowSpan={2}>
            Project
          </th>
          <th style={{width: "12em"}} rowSpan={2}>
            Document
          </th>
          <th style={{width: "4em"}} rowSpan={2}>
            Status
          </th>
          <th style={{width: "4em"}} rowSpan={2}>
            Tests
          </th>
          <th style={{width: "9em"}} colSpan={3}>
            Stats
          </th>
          <th style={{width: "4em"}} rowSpan={2}>
            Actions
          </th>
        </tr>
        <tr>
          <StatsSubTh>Runs</StatsSubTh>
          <StatsSubTh>Duration</StatsSubTh>
          <StatsSubTh>Cost</StatsSubTh>
        </tr>
      </thead>
      <tbody>
        {renderDataAssetRuns(
          dataAssetRuns,
          isLoading,
          error,
          organisationId,
          filterByRunEntity,
          onViewOutput,
          selectedDataAssetRun,
          updateFilterValue,
          showOnlyTopLevelRuns,
        )}
      </tbody>
    </Table>
  );
}

function renderDataAssetRuns(
  dataAssetRuns: DataAssetOverview[],
  isLoading: boolean,
  error: string | null,
  organisationId: OrganisationId,
  filterByRunEntity: FilterByRunEntity,
  onViewOutput,
  selectedDataAssetRun,
  updateFilterValue,
  showOnlyTopLevelRuns,
) {
  if (isLoading) {
    return <DataAssetRunsSkeleton />;
  }

  if (error) {
    return (
      <tr>
        <TableCell
          colSpan={11}
          style={{padding: "1em", color: "#f44336", textAlign: "center"}}
        >
          Error: {error}
        </TableCell>
      </tr>
    );
  }

  if (dataAssetRuns.length === 0) {
    return (
      <tr>
        <TableCell colSpan={11} style={{padding: "1em", textAlign: "center"}}>
          No runs for this entity.
        </TableCell>
      </tr>
    );
  }

  const filteredRuns = showOnlyTopLevelRuns
    ? dataAssetRuns.filter(run => !run.info.has_downstream_runs)
    : dataAssetRuns;

  return filteredRuns.map((run, idx) =>
    renderDataAssetRun(
      run,
      idx,
      organisationId,
      onViewOutput,
      selectedDataAssetRun,
      filterByRunEntity,
      updateFilterValue,
    ),
  );
}

function renderDataAssetRun(
  run: DataAssetOverview,
  idx: number,
  organisationId: OrganisationId,
  onViewOutput,
  selectedDataAssetRun,
  filterByRunEntity: FilterByRunEntity,
  updateFilterValue: (
    pairs: Record<string, unknown | undefined | null>,
  ) => void,
) {
  const {config, definition, info} = run;
  const time = new Date(info.creation_date).toLocaleTimeString();
  const date = new Date(info.creation_date).toLocaleDateString();
  const configTime = new Date(config.creation_date).toLocaleTimeString();
  const configDate = new Date(info.creation_date).toLocaleDateString();

  const redirectToCreateView = loc =>
    loc.pathname.replace(
      "overview",
      `definition/${definition.id}/config/${config.id}${getQueryParams({
        project: info.project.id,
        document: info.document.id,
      })}`,
    );

  const isSelected =
    selectedDataAssetRun && selectedDataAssetRun.config.id === config.id;

  return (
    <TableRow key={`asset-run-${idx}`} $isSelected={isSelected}>
      <MediumCell>
        <ListLink to={redirectToCreateView}>
          <Tooltip title={info.name ?? "N/A"}>
            <span
              style={{
                fontWeight: !info.has_downstream_runs ? "bold" : "normal",
              }}
            >
              {info.name ?? "N/A"}
            </span>
          </Tooltip>
        </ListLink>
      </MediumCell>
      <MediumCell>
        <Tooltip title={definition.name}>
          <ListLink to={redirectToCreateView}>{definition.name}</ListLink>
        </Tooltip>
      </MediumCell>
      <TableCell style={{textAlign: "left"}}>
        <div style={{display: "flex", alignItems: "center", gap: "1em"}}>
          <ListLink to={redirectToCreateView}>
            <span>{config.name}</span>
            <br />
            <span
              style={{fontSize: "0.8em", whiteSpace: "nowrap"}}
            >{`${configDate} ${configTime}`}</span>
          </ListLink>
          {config.is_public && (
            <Tooltip title="Public Config">
              <VisibilityIcon />
            </Tooltip>
          )}
        </div>
      </TableCell>
      <MediumCell>
        <Tooltip title={`${info.username}, ${date} ${time}`}>
          <FilterableName
            filter={() => updateFilterValue({user: info.username})}
          >
            <ListLink to={redirectToCreateView}>
              <span>{info.username}</span>{" "}
              <span
                style={{fontSize: "0.8em", whiteSpace: "nowrap"}}
              >{`${date} ${time}`}</span>
            </ListLink>
          </FilterableName>
        </Tooltip>
      </MediumCell>
      <TableCell>
        <FilterableName
          name={info.project.name}
          filter={() => filterByRunEntity(info.project.id)}
        />
      </TableCell>
      <TableCell>
        <FilterableName
          name={info.document.name}
          filter={() => filterByRunEntity(info.project.id, info.document.id)}
        />
      </TableCell>
      <TableCell>
        <ListLink
          to={`/organisation/${organisationId}/llm_run/${info.last_llm_run_id}/detail`}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {getStatusIcon(info)}
            <IconButton size="small" style={{padding: 0}}>
              <LinkIcon />
            </IconButton>
          </div>
        </ListLink>
      </TableCell>
      <TableCell>
        <TestStatus stats={info.test_stats} />
      </TableCell>
      <TableCell>
        <ListLink to={redirectToCreateView}>{info.runs}</ListLink>
      </TableCell>
      <TableCell>
        <ListLink to={redirectToCreateView}>
          {renderDuration(info.duration)}
        </ListLink>
      </TableCell>
      <TableCell>
        <ListLink to={redirectToCreateView}>{`\$${info.cost.toFixed(
          4,
        )}`}</ListLink>
      </TableCell>
      <TableCell>
        <Tooltip title="View Output">
          <IconButton
            size="small"
            onClick={() => onViewOutput(run)}
            style={{padding: 0}}
          >
            <MoveToInboxIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
